import React, { useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { HiPencilSquare, HiArrowPath, HiExclamationTriangle } from 'react-icons/hi2';
import { API_BASE } from '../../config';
import RpePainFields, { rpeLabel } from './RpePainFields';

const readFeedback = (debrief) => {
  const fb = debrief?.athlete_feedback || null;
  return {
    text: String(fb?.text ?? debrief?.user_feedback ?? '').trim(),
    rpe: Number.isFinite(Number(fb?.rpe)) && Number(fb?.rpe) >= 1 ? Number(fb.rpe) : null,
    pain: typeof fb?.pain === 'boolean' ? fb.pain : null,
    pain_location: fb?.pain_location || '',
    source: fb?.source || null
  };
};

/**
 * "Ton ressenti": athlete feedback on a completed session (text, RPE, pain), editable by the
 * owner, with an optional re-run of the coach analysis (POST /workouts/:id/reanalyze).
 */
export default function AthleteFeedbackEditor({
  workoutId,
  debrief,
  canEdit,
  isEditing,
  onEditingChange,
  onSaved,
  onRerunStart,
  onRerunEnd
}) {
  const { t } = useTranslation();
  const current = readFeedback(debrief);
  const hasFeedback = Boolean(current.text || current.rpe != null || current.pain != null);
  const isStrava = debrief?.execution_type === 'strava' || Boolean(debrief?.strava_activity_id);

  const [text, setText] = useState(current.text);
  const [fields, setFields] = useState({ rpe: current.rpe, pain: current.pain, pain_location: current.pain_location });
  const [saving, setSaving] = useState(null); // 'save' | 'rerun'
  const [error, setError] = useState('');

  // Reset the form from the stored feedback whenever the editor opens.
  useEffect(() => {
    if (!isEditing) return;
    const fb = readFeedback(debrief);
    setText(fb.text);
    setFields({ rpe: fb.rpe, pain: fb.pain, pain_location: fb.pain_location });
    setError('');
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isEditing]);

  const submit = async (rerun) => {
    if (saving) return;
    setSaving(rerun ? 'rerun' : 'save');
    setError('');
    if (rerun) onRerunStart?.();
    try {
      const res = await fetch(`${API_BASE}/workouts/${workoutId}/reanalyze`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        credentials: 'include',
        body: JSON.stringify({
          feedback: String(text || '').trim(),
          rpe: fields.rpe ?? null,
          pain: fields.pain ?? null,
          pain_location: fields.pain === true ? (String(fields.pain_location || '').trim() || null) : null,
          rerun
        })
      });
      const data = await res.json().catch(() => ({}));
      if (!res.ok) throw new Error(data?.error || t('feedback.save_error'));
      onSaved?.(data?.debrief || null, { rerun, reanalyzed: Boolean(data?.reanalyzed) });
      onEditingChange?.(false);
    } catch (e) {
      setError(e?.message || t('feedback.save_error'));
    } finally {
      setSaving(null);
      if (rerun) onRerunEnd?.();
    }
  };

  if (isEditing && canEdit) {
    return (
      <div id="athlete-feedback" className="relative bg-white p-5 md:p-6 rounded-3xl border-2 border-indigo-100 shadow-sm">
        <div className="flex items-center justify-between gap-3 mb-4">
          <h4 className="font-black text-gray-900 text-lg tracking-tight">{t('feedback.title')}</h4>
        </div>

        <label className="block text-xs font-black text-gray-500 uppercase tracking-widest mb-2">
          {t('workout.feedback_label')} <span className="normal-case tracking-normal text-gray-400 font-bold">({t('workout.optional')})</span>
        </label>
        <textarea
          rows={4}
          disabled={Boolean(saving)}
          value={text}
          onChange={(e) => setText(e.target.value)}
          placeholder={t('workout.user_feedback_placeholder')}
          className="w-full border border-gray-200 p-4 rounded-2xl text-base font-medium focus:ring-4 focus:ring-indigo-500/10 focus:border-indigo-500 outline-none transition-all resize-none bg-gray-50 focus:bg-white placeholder-gray-400 leading-relaxed"
        />

        <div className="mt-5">
          <RpePainFields value={fields} onChange={setFields} disabled={Boolean(saving)} />
        </div>

        {error ? (
          <div className="mt-4 rounded-xl border border-red-200 bg-red-50 p-3 text-sm font-bold text-red-700">{error}</div>
        ) : null}

        {saving === 'rerun' ? (
          <div className="mt-5 rounded-2xl border border-indigo-100 bg-indigo-50 p-4 flex items-center gap-3">
            <span className="animate-spin h-5 w-5 border-2 border-indigo-600 border-t-transparent rounded-full shrink-0" />
            <div>
              <div className="text-sm font-black text-indigo-900">{t('feedback.rerun_loading')}</div>
              <div className="text-xs text-indigo-700 mt-0.5">{t('feedback.rerun_loading_hint')}</div>
            </div>
          </div>
        ) : null}

        <div className="mt-5 flex flex-col-reverse sm:flex-row sm:justify-end gap-2">
          <button
            type="button"
            disabled={Boolean(saving)}
            onClick={() => onEditingChange?.(false)}
            className="px-5 py-2.5 text-gray-600 font-bold hover:bg-gray-100 rounded-full transition-colors text-sm"
          >
            {t('workout.cancel')}
          </button>
          <button
            type="button"
            disabled={Boolean(saving)}
            onClick={() => submit(false)}
            className="px-5 py-2.5 rounded-full font-bold text-sm border-2 border-indigo-200 text-indigo-700 bg-white hover:bg-indigo-50 disabled:opacity-60"
          >
            {saving === 'save' ? t('workout.saving') : t('feedback.save')}
          </button>
          <button
            type="button"
            disabled={Boolean(saving)}
            onClick={() => submit(true)}
            className="px-5 py-2.5 rounded-full font-black text-sm text-white bg-indigo-600 hover:bg-indigo-700 shadow-lg shadow-indigo-500/30 disabled:opacity-60 inline-flex items-center justify-center gap-2"
          >
            <HiArrowPath className={`w-4 h-4 ${saving === 'rerun' ? 'animate-spin' : ''}`} />
            {t('feedback.save_and_rerun')}
          </button>
        </div>
      </div>
    );
  }

  // Display mode
  if (!hasFeedback) {
    if (!canEdit) return null;
    if (isStrava) {
      return (
        <div id="athlete-feedback" className="rounded-3xl border-2 border-orange-200 bg-orange-50 p-5 md:p-6 flex flex-col md:flex-row md:items-center gap-4">
          <div className="flex-1">
            <div className="text-base font-black text-orange-900">{t('feedback.strava_cta_title')}</div>
            <div className="text-sm text-orange-800 mt-1 leading-relaxed">{t('feedback.strava_cta_hint')}</div>
          </div>
          <button
            type="button"
            onClick={() => onEditingChange?.(true)}
            className="shrink-0 px-5 py-3 rounded-full font-black text-sm text-white bg-orange-600 hover:bg-orange-700 shadow-lg shadow-orange-500/30 inline-flex items-center justify-center gap-2"
          >
            <HiPencilSquare className="w-4 h-4" /> {t('feedback.add_cta')}
          </button>
        </div>
      );
    }
    return (
      <div id="athlete-feedback" className="rounded-3xl border border-dashed border-gray-200 bg-gray-50/60 p-5 flex flex-col sm:flex-row sm:items-center gap-3">
        <div className="flex-1 text-sm text-gray-500 font-medium">{t('feedback.empty_hint')}</div>
        <button
          type="button"
          onClick={() => onEditingChange?.(true)}
          className="shrink-0 px-4 py-2 rounded-full font-bold text-sm text-indigo-700 bg-white border border-indigo-200 hover:bg-indigo-50 inline-flex items-center justify-center gap-2"
        >
          <HiPencilSquare className="w-4 h-4" /> {t('feedback.add_cta')}
        </button>
      </div>
    );
  }

  return (
    <div id="athlete-feedback" className="bg-gray-50/80 p-5 md:p-6 rounded-3xl border border-gray-100">
      <div className="flex items-start justify-between gap-3 mb-3">
        <div className="flex items-center gap-2 flex-wrap">
          <h4 className="font-black text-gray-400 text-xs uppercase tracking-widest">{t('feedback.title')}</h4>
          {current.source === 'strava' ? (
            <span className="px-2 py-0.5 rounded-full text-[10px] font-black uppercase bg-orange-50 text-orange-700 border border-orange-200">
              {t('feedback.from_strava')}
            </span>
          ) : null}
        </div>
        {canEdit ? (
          <button
            type="button"
            onClick={() => onEditingChange?.(true)}
            className="shrink-0 inline-flex items-center gap-1.5 px-3 py-1.5 rounded-full text-xs font-bold text-indigo-700 bg-white border border-indigo-100 hover:bg-indigo-50"
          >
            <HiPencilSquare className="w-4 h-4" /> {t('feedback.edit')}
          </button>
        ) : null}
      </div>
      {current.text ? (
        <p className="text-gray-700 italic text-lg leading-relaxed whitespace-pre-line">"{current.text}"</p>
      ) : null}
      {(current.rpe != null || current.pain != null) ? (
        <div className="mt-3 flex flex-wrap gap-2">
          {current.rpe != null ? (
            <span className="px-3 py-1 rounded-full bg-white border border-gray-200 text-xs font-black text-gray-700">
              {t('feedback.rpe_short')} {current.rpe}/10 · {rpeLabel(t, current.rpe)}
            </span>
          ) : null}
          {current.pain === true ? (
            <span className="px-3 py-1 rounded-full bg-red-50 border border-red-200 text-xs font-black text-red-700 inline-flex items-center gap-1">
              <HiExclamationTriangle className="w-3.5 h-3.5" />
              {current.pain_location ? t('feedback.pain_at', { location: current.pain_location }) : t('feedback.pain_yes')}
            </span>
          ) : current.pain === false ? (
            <span className="px-3 py-1 rounded-full bg-emerald-50 border border-emerald-200 text-xs font-black text-emerald-700">
              {t('feedback.pain_no')}
            </span>
          ) : null}
        </div>
      ) : null}
    </div>
  );
}
